import { Handle, Position } from '@xyflow/react'

/**
 * EyeFollowNode — face-graph source that points the EYES at the camera, and
 * hands a share of the same aim to the neck (ENG-1060).
 *
 * The 70/30 default is Unreal parity: there, eye contact was 70% eye rotation
 * and 30% neck. Both weights live on ONE node because they are two shares of a
 * single aim — a separate Head Follow node placed at 0.3 by hand would express
 * the same numbers with nothing keeping them in that relationship.
 *
 * Strength comes from the parent blend path (sentinel `__eyefollow__`), like
 * Head Follow — wire through a Blend with `alphaVariable` to fade eye contact in
 * and out from the Event Graph. Every field is also a pin, so `Eye Weight` can be
 * driven straight from a variable or a Random node.
 */

const PARAMS = [
  { field: 'eyeWeight',   label: 'Eye Weight',  min: 0,    max: 1,  step: 0.05, def: 0.7,  unit: '',
    title: 'How much of the aim the EYES carry. 0.7 = Unreal parity. At 0 the eyes stay put and only the neck turns.' },
  { field: 'neckWeight',  label: 'Neck Weight', min: 0,    max: 1,  step: 0.05, def: 0.3,  unit: '',
    title: 'How much of the aim the NECK carries. Handed to the same neck-aim code Head Follow uses, so the two never disagree. 0 = eyes only.' },
  { field: 'maxYaw',      label: 'Max Yaw',     min: 0,    max: 60, step: 1,    def: 35,   unit: '°',
    title: 'How far the eyes can travel left/right, in degrees. A gaze-conditioned eye branch also applies its own cone limit, and the tighter of the two wins.' },
  { field: 'maxPitch',    label: 'Max Pitch',   min: 0,    max: 45, step: 1,    def: 25,   unit: '°',
    title: 'How far the eyes can travel up/down, in degrees.' },
  { field: 'smoothing',   label: 'Smoothing',   min: 0.01, max: 1,  step: 0.01, def: 0.35, unit: '',
    title: 'How fast the eyes react. Higher = snappier. Eyes are near-instant on a real face, so this defaults well above Head Follow.' },
  { field: 'deadbandDeg', label: 'Dead Zone',   min: 0,    max: 10, step: 0.5,  def: 1,    unit: '°',
    title: 'The camera must move this far from where the eyes are aimed before they re-target. Filters jitter. 0 reacts to every motion.' },
]

const DEG = 180 / Math.PI

export function EyeFollowNode({ data, selected }) {
  const vals = {}
  for (const p of PARAMS) vals[p.field] = data[p.field] ?? p.def

  const weight = data._debugWeight ?? 0
  const yaw    = data._debugYaw   ?? 0
  const pitch  = data._debugPitch ?? 0
  // Set by BlueprintEditor from eyeFollowSupport(): a splat or streamed
  // character has no rig for the ARKit eye morphs to land on, so the node would
  // otherwise sit here looking live and drive nothing. Shown and disabled WITH
  // the reason — never hidden, never left looking functional.
  const unsupported = data._unsupportedReason ?? null
  const active = !unsupported && weight > 0.001

  return (
    <div style={{
      background: selected ? '#0f1a24' : '#08141c',
      border: `2px solid ${active ? '#30c0e0' : selected ? '#2080b0' : '#184858'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 205,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
      boxShadow: active ? '0 0 14px rgba(48,192,224,0.25)' : 'none',
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }}>👁</span>
        <span style={{ color: '#40c0e0', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>EYE FOLLOW</span>
        {unsupported
          ? <span style={{ color: '#c08040', fontSize: 9, marginLeft: 'auto', letterSpacing: 0.3 }}>⚠ unavailable</span>
          : active
            ? <span style={{ color: '#30c0e0', fontSize: 9, marginLeft: 'auto', letterSpacing: 0.3 }}>● {(weight * 100).toFixed(0)}%</span>
            : <span style={{ color: '#2a4858', fontSize: 9, marginLeft: 'auto' }}>○ idle</span>}
      </div>

      {/* The reason, inline and next to the controls it disables */}
      {unsupported && (
        <div style={{
          background: '#241a0e', border: '1px solid #6a4a1e', borderRadius: 4,
          color: '#e0b070', fontSize: 9, lineHeight: 1.4, padding: '5px 7px', marginBottom: 8,
        }}>
          {unsupported}
        </div>
      )}

      {/* Config rows — each field is also a pin (Unreal convention: every typed
          input is a pin OR an inline value) */}
      {PARAMS.map(({ field, label, min, max, step, unit, title }) => (
        <div key={field} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, position: 'relative' }}>
          <Handle
            type="target"
            position={Position.Left}
            id={`in-${field}`}
            style={{ background: '#4ecf7e', width: 7, height: 7, top: '50%', transform: 'translateY(-50%)' }}
          />
          <span title={title} style={{ color: '#90a8b8', fontSize: 10, flex: 1, marginLeft: 12, cursor: 'help' }}>{label}</span>
          <input
            type="number"
            min={min} max={max} step={step}
            value={vals[field]}
            disabled={!!unsupported}
            onChange={e => data.onChange?.(field, parseFloat(e.target.value))}
            onMouseDown={e => e.stopPropagation()}
            style={{
              width: 48, boxSizing: 'border-box',
              background: '#0a1520', border: '1px solid #1f3850',
              color: unsupported ? '#4a5a66' : '#9ad0f0', padding: '2px 4px',
              borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
              cursor: unsupported ? 'not-allowed' : 'auto',
            }}
          />
          <span style={{ color: '#405060', fontSize: 9, width: 12, textAlign: 'right' }}>{unit}</span>
        </div>
      ))}

      {/* Live readout — the aim, and how the gate is actually being spent */}
      <div style={{ color: '#306878', fontSize: 9, margin: '6px 0' }}>
        yaw {(yaw * DEG).toFixed(0)}° · pitch {(pitch * DEG).toFixed(0)}°
        <span style={{ marginLeft: 6, color: '#1f4858' }}>
          (eye {(weight * vals.eyeWeight).toFixed(2)} / neck {(weight * vals.neckWeight).toFixed(2)})
        </span>
      </div>

      {/* Weight bar */}
      <div style={{ height: 3, background: '#0a1a20', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(1, weight) * 100}%`,
          height: '100%',
          background: active ? '#30c0e0' : '#184858',
          transition: 'width 0.1s linear',
        }} />
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="anim"
        style={{ background: active ? '#30c0e0' : '#184858', width: 10, height: 10 }}
      />
    </div>
  )
}
